'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import discardFields from '@haaretz/s-common-utils/discardFields';
import Head from 'next/head';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import s9 from 'style9';
import GifAsVideo from './GifAsVideo';
const c = s9.create({
    base: {
        color: 'var(--altColor, transparent)'
    }
});
const allImgs = new Map();
let perfObserver;
if (typeof window === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    global.__NEXT_IMAGE_IMPORTED = true;
}
const VALID_LOADING_VALUES = [
    'lazy',
    'eager',
    undefined
];
// eslint-disable-next-line @typescript-eslint/no-empty-function
let warnOnce = (_)=>{};
if (process.env.NODE_ENV !== 'production') {
    const warnings = new Set();
    warnOnce = (msg)=>{
        if (!warnings.has(msg)) {
            console.warn(msg);
        }
        warnings.add(msg);
    };
}
// See https://stackoverflow.com/q/39777833/266535 for why we use this ref
// handler instead of the img's onLoad attribute.
function handleLoading(img, src, onLoadingCompleteRef) {
    if (!img || img['data-loaded-src'] === src) {
        return;
    }
    img['data-loaded-src'] = src;
    const p = 'decode' in img ? img.decode() : Promise.resolve();
    p.catch(()=>{
        // eslint-disable-next-line no-console
        console.log(`Failed decoding ${img.src}`);
    }).then(()=>{
        if (!img.parentNode) {
            // Exit early in case of race condition:
            // - onload() is called
            // - decode() is called but incomplete
            // - unmount is called
            // - decode() completes
            return;
        }
        if (onLoadingCompleteRef?.current) {
            onLoadingCompleteRef.current(img);
        }
        if (process.env.NODE_ENV !== 'production') {
            const heightModified = img.height.toString() !== img.getAttribute('height');
            const widthModified = img.width.toString() !== img.getAttribute('width');
            if (heightModified && !widthModified || !heightModified && widthModified) {
                warnOnce(`Image with src "${src}" has either width or height modified, but not the other. If you use CSS to change the size of your image, also include the styles 'width: "auto"' or 'height: "auto"' to maintain the aspect ratio.`);
            }
        }
    });
}
const ImageElement = ({ src, sizes, srcSet, inlineStyle, isLazy, loading, width, height, styleExtend = [], onLoadingCompleteRef, setShowAltText, onLoad, onError, ...attrs })=>{
    return /* eslint-disable @next/next/no-img-element, jsx-a11y/alt-text */ /*#__PURE__*/ _jsx("img", {
        // NOTE: Ignore image content in chromatic snapshots
        //       because scaled rendering fucks them up
        "data-chromatic": "ignore",
        ...attrs,
        width: width,
        height: height,
        sizes: sizes,
        src: src,
        srcSet: srcSet,
        decoding: "async",
        className: s9(c.base, ...styleExtend),
        loading: isLazy ? 'lazy' : loading,
        style: {
            ...inlineStyle
        },
        ref: useCallback((img)=>{
            if (!img) {
                return;
            }
            if (onError) {
                // If the image has an error before react hydrates, then the error is lost.
                // The workaround is to wait until the image is mounted which is after hydration,
                // then we set the src again to trigger the error handler (if there was an error).
                // eslint-disable-next-line no-self-assign
                img.src = img.src;
            }
            if (process.env.NODE_ENV !== 'production') {
                if (!src) {
                    console.error('Image is missing required "src" property:', img);
                }
                if (img.getAttribute('alt') === null) {
                    console.error('Image is missing required "alt" property. Please add Alternative Text to describe the image for screen readers and search engines.');
                }
            }
            if (img.complete) {
                handleLoading(img, src, onLoadingCompleteRef);
            }
        }, [
            src,
            onLoadingCompleteRef,
            onError
        ]),
        onError: (event)=>{
            // if the real image fails to load, this will ensure "alt" is visible
            setShowAltText(true);
            if (onError) {
                onError(event);
            }
        }
    });
};
export const resetArgs = [
    '__typename',
    'files',
    'type',
    'fbCaption',
    'fbCredit',
    'htzUrl',
    'playbackBehaviour',
    'publicUrl',
    'kind'
];
export default function Image({ src, srcSet, sizes, priority = false, loading, width, height, inlineStyle, styleExtend = [], imgData, onLoad, posterSrc, ...rest }) {
    const isLazy = !priority && (loading === 'lazy' || typeof loading === 'undefined');
    const [showAltText, setShowAltText] = useState(false);
    const onLoadingCompleteRef = useRef(onLoad);
    useEffect(()=>{
        onLoadingCompleteRef.current = onLoad;
    }, [
        onLoad
    ]);
    if (rest.kind === 'video') {
        return /*#__PURE__*/ _jsx(GifAsVideo, {
            src: src,
            inlineStyle: showAltText ? {
                '--altColor': "rgb(var(--c-neutral900))",
                ...inlineStyle
            } : inlineStyle,
            loading: loading,
            srcSet: srcSet,
            width: width,
            posterSrc: posterSrc ?? '',
            height: height,
            styleExtend: styleExtend,
            onLoadingCompleteRef: onLoadingCompleteRef,
            setShowAltText: setShowAltText,
            onError: rest.onError,
            title: rest.title,
            ...rest,
            alt: rest.alt || ''
        });
    }
    if (process.env.NODE_ENV !== 'production') {
        if (!VALID_LOADING_VALUES.includes(loading)) {
            console.error(new Error(`Image with src "${src}" has invalid "loading" property. Provided "${loading}" should be one of ${VALID_LOADING_VALUES.map(String).join(',')}.`));
        }
        if (typeof width === 'undefined') {
            console.error(new Error(`Image with src "${src}" is missing required "width" property.`));
        } else if (isNaN(width)) {
            console.error(new Error(`Image with src "${src}" has invalid "width" property. Expected a numeric value in pixels but received "${width}".`));
        }
        if (typeof height === 'undefined') {
            console.error(new Error(`Image with src "${src}" is missing required "height" property.`));
        } else if (isNaN(height)) {
            console.error(new Error(`Image with src "${src}" has invalid "height" property. Expected a numeric value in pixels but received "${height}".`));
        }
        if (priority && loading === 'lazy') {
            console.error(new Error(`Image with src "${src}" has both "priority" and "loading='lazy'" properties. Only one should be used.`));
        }
        if ('ref' in rest) {
            warnOnce(`Image with src "${src}" is using unsupported "ref" property. Consider using the "onLoad" property instead.`);
        }
        if (typeof window !== 'undefined' && !perfObserver && window.PerformanceObserver) {
            // eslint-disable-next-line react-compiler/react-compiler
            perfObserver = new PerformanceObserver((entryList)=>{
                for (const entry of entryList.getEntries()){
                    // @ts-expect-error - missing "LargestContentfulPaint" class with "element" prop
                    const imgSrc = entry?.element?.src || '';
                    const lcpImage = allImgs.get(imgSrc);
                    if (lcpImage && !lcpImage.priority) {
                        // https://web.dev/lcp/#measure-lcp-in-javascript
                        warnOnce(`Image with src "${lcpImage.src}" was detected as the Largest Contentful Paint (LCP). Please add the "priority" property if this image is above the fold.` + '\nRead more: https://nextjs.org/docs/api-reference/next/image#priority');
                    }
                }
            });
            try {
                perfObserver.observe({
                    type: 'largest-contentful-paint',
                    buffered: true
                });
            } catch (err) {
                // Log error but don't crash the app
                console.error(err);
            }
        }
    }
    if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
        allImgs.set(src, {
            src,
            priority
        });
    }
    let imageSrcSetPropName = 'imagesrcset';
    let imageSizesPropName = 'imagesizes';
    if (process.env.__NEXT_REACT_ROOT || process.env.NODE_ENV === 'test') {
        imageSrcSetPropName = 'imageSrcSet';
        imageSizesPropName = 'imageSizes';
    }
    const linkProps = {
        [imageSrcSetPropName]: srcSet,
        [imageSizesPropName]: sizes,
        crossOrigin: rest.crossOrigin
    };
    const { kind, type, ...restProps } = rest;
    const imgElementAttrs = {
        isLazy,
        src,
        sizes,
        srcSet,
        inlineStyle: showAltText ? {
            '--altColor': "rgb(var(--c-neutral900))",
            ...inlineStyle
        } : inlineStyle,
        styleExtend,
        loading,
        width,
        height,
        onLoadingCompleteRef,
        setShowAltText,
        ...restProps
    };
    if (priority) imgElementAttrs.fetchPriority = 'high';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(ImageElement, {
                ...discardFields(imgElementAttrs, resetArgs)
            }),
            // Note how we omit the `href` attribute, as it would only be relevant
            // for browsers that do not support `imagesrcset`, and in those cases
            // it would likely cause the incorrect image to be preloaded.
            //
            // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
            priority ? /*#__PURE__*/ _jsx(Head, {
                children: /*#__PURE__*/ _jsx("link", {
                    rel: "preload",
                    as: "image",
                    href: srcSet ? undefined : src,
                    ...linkProps
                }, `__nimg-${src}${srcSet}${sizes}`)
            }) : null
        ]
    });
}
const colorCssVariablesHtz = [
    "neutral900"
];

//# sourceMappingURL=CustomNextImage.js.map